import { useState, useEffect, useCallback, useRef } from "react";
import { reportAPI } from "../utils/api";
import { useApp } from "../context/AppContext";

function playSound(type) {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    if (type === "critical") {
      [0, 0.3, 0.6].forEach(delay => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain); gain.connect(ctx.destination);
        osc.frequency.value = 880; osc.type = "square";
        gain.gain.setValueAtTime(0.3, ctx.currentTime + delay);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + delay + 0.25);
        osc.start(ctx.currentTime + delay); osc.stop(ctx.currentTime + delay + 0.25);
      });
    } else {
      const osc = ctx.createOscillator(); const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.frequency.value = 523; osc.type = "sine";
      gain.gain.setValueAtTime(0.2, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.8);
      osc.start(ctx.currentTime); osc.stop(ctx.currentTime + 0.8);
    }
  } catch (e) {}
}

const STATUS_COLORS = {
  "Critical High": { bg: "#FEE2E2", color: "#991B1B", border: "#FCA5A5" },
  "Critical Low":  { bg: "#FEE2E2", color: "#991B1B", border: "#FCA5A5" },
  "Needs Review":  { bg: "#FEF3C7", color: "#92400E", border: "#FCD34D" },
  "High":          { bg: "#FEF3C7", color: "#92400E", border: "#FCD34D" },
  "Low":           { bg: "#DBEAFE", color: "#1D4ED8", border: "#93C5FD" },
  "Normal":        { bg: "#DCFCE7", color: "#15803D", border: "#86EFAC" },
};

export default function ClinicalReview() {
  const { user } = useApp();
  const [tab, setTab]             = useState("pending"); // "pending" | "reviewed"
  const [data, setData]           = useState(null);
  const [reviewed, setReviewed]   = useState(null);
  const [loading, setLoading]     = useState(true);
  const [filter, setFilter]       = useState("all");
  const [actioning, setActioning] = useState(null);
  const [search, setSearch]       = useState("");
  const [updatingStatus, setUpdatingStatus] = useState(null);
  const [clearing, setClearing]   = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const soundPlayed = useRef(false);
  const prevCritical = useRef(0);

  const canAction = user?.role === "admin" || user?.role === "doctor";

  const loadPending = useCallback(async () => {
    try {
      const res = await reportAPI.getCriticalReview();
      setData(res);
      const newCritical = res.critical_count || 0;
      if (!soundPlayed.current) {
        soundPlayed.current = true;
        // Don't auto-play on initial load — requires user gesture
      } else if (newCritical > prevCritical.current) {
        playSound("critical");
      }
      prevCritical.current = newCritical;
    } catch (e) {
      setData({ reports: [], total: 0, critical_count: 0, needs_review_count: 0 });
    } finally { setLoading(false); }
  }, []);

  const loadReviewed = useCallback(async () => {
    try {
      const res = await reportAPI.getReviewedReports();
      setReviewed(res);
    } catch (e) { setReviewed({ reports: [], total: 0 }); }
  }, []);

  useEffect(() => {
    loadPending();
    loadReviewed(); // load on mount to get count
    const t = setInterval(loadPending, 120000);
    return () => clearInterval(t);
  }, [loadPending, loadReviewed]);
  useEffect(() => { if (tab === "reviewed") loadReviewed(); }, [tab, loadReviewed]);

  const handleAction = async (reportId) => {
    // Optimistic update — remove immediately
    const report = data?.reports?.find(r => r.report_id === reportId);
    setData(prev => ({
      ...prev,
      reports: prev.reports.filter(r => r.report_id !== reportId),
      total: Math.max(0, prev.total - 1),
      critical_count: Math.max(0, prev.critical_count - (report?.has_critical ? 1 : 0)),
      needs_review_count: Math.max(0, prev.needs_review_count - (report?.has_critical ? 0 : 1)),
    }));
    // Update reviewed count optimistically
    setReviewed(prev => prev ? { ...prev, total: (prev.total || 0) + 1 } : { reports: [], total: 1 });
    setActioning(reportId);
    try {
      await reportAPI.actionReport(reportId);
      // Refresh reviewed list in background
      loadReviewed();
    } catch (e) {
      // Rollback on error
      loadPending();
    } finally { setActioning(null); }
  };

  const handleStatusChange = async (reportId, paramName, newStatus) => {
    const key = `${reportId}:${paramName}`;
    setUpdatingStatus(key);
    try {
      await reportAPI.updateParamStatus(reportId, paramName, newStatus);
      setData(prev => ({
        ...prev,
        reports: prev.reports.map(r => r.report_id !== reportId ? r : {
          ...r, values: r.values.map(v => v.name !== paramName ? v : { ...v, status: newStatus })
        })
      }));
    } finally { setUpdatingStatus(null); }
  };

  const handleClear = async () => {
    setClearing(true);
    try {
      await reportAPI.clearReviewed();
      setReviewed({ reports: [], total: 0 });
      setShowClearConfirm(false);
    } catch (e) {} finally { setClearing(false); }
  };

  const handleViewReport = (report) => {
    sessionStorage.setItem("emr_selected_patient", report.patient_id);
    // Navigate to specific report if we have report_id
    // Use pt_pd_* scoped keys (Patient tab context)
    if (report.report_id) {
      sessionStorage.setItem("pt_pd_rep", report.report_id);
      sessionStorage.setItem("pt_pd_view", "report");
      sessionStorage.setItem("pt_pd_type", report.type);
      sessionStorage.setItem("pt_pd_context", "patient");
    } else {
      sessionStorage.setItem("pt_pd_view", "categories");
      sessionStorage.removeItem("pt_pd_type");
      sessionStorage.removeItem("pt_pd_rep");
    }
    // Clear old keys just in case
    sessionStorage.removeItem("emr_pd_rep");
    sessionStorage.removeItem("emr_pd_view");
    sessionStorage.removeItem("emr_pd_type");
    window.dispatchEvent(new CustomEvent("emr:navigate", { detail: { page: "patients" } }));
  };

  const filtered = (data?.reports || []).filter(r => {
    if (filter === "critical")     { if (!r.has_critical) return false; }
    if (filter === "needs_review") { if (r.has_critical) return false; }
    if (search.trim()) {
      const q = search.toLowerCase();
      return r.patient_name?.toLowerCase().includes(q) ||
             r.patient_id?.toLowerCase().includes(q) ||
             r.type?.toLowerCase().includes(q) ||
             r.lab_id?.toLowerCase().includes(q);
    }
    return true;
  });

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: "#64748B", fontSize: 13 }}>Loading clinical review...</div>;

  const criticalCount    = data?.critical_count || 0;
  const needsReviewCount = data?.needs_review_count || 0;
  const reviewedCount    = reviewed?.total || 0;

  return (
    <div style={{ padding: "24px 28px", maxWidth: 1200, margin: "0 auto" }}>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 800, color: "#0F172A", margin: 0 }}>Clinical Review</h1>
          <p style={{ fontSize: 13, color: "#64748B", margin: "4px 0 0" }}>Reports requiring immediate attention or manual review</p>
        </div>
        <button className="btn btn-ghost btn-sm" onClick={() => { loadPending(); if (tab === "reviewed") loadReviewed(); }} style={{ fontSize: 12 }}>↻ Refresh</button>
      </div>

      {/* Summary cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 14, marginBottom: 20 }}>
        <div onClick={() => { setTab("pending"); setFilter("critical"); }}
          style={{ background: criticalCount > 0 ? "#FEE2E2" : "#F8FAFC", border: `1px solid ${criticalCount > 0 ? "#FCA5A5" : "#E2E8F0"}`, borderRadius: 10, padding: "14px 18px", cursor: "pointer" }}>
          {criticalCount > 0 && <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#DC2626", display: "inline-block", animation: "pulse 1s infinite" }} />
            <span style={{ fontSize: 10, fontWeight: 700, color: "#DC2626", textTransform: "uppercase", letterSpacing: 1 }}>Urgent</span>
          </div>}
          <div style={{ fontSize: 28, fontWeight: 800, color: criticalCount > 0 ? "#DC2626" : "#94A3B8" }}>{criticalCount}</div>
          <div style={{ fontSize: 12, color: "#64748B", fontWeight: 500 }}>Critical Reports</div>
        </div>
        <div onClick={() => { setTab("pending"); setFilter("needs_review"); }}
          style={{ background: needsReviewCount > 0 ? "#FFFBEB" : "#F8FAFC", border: `1px solid ${needsReviewCount > 0 ? "#FCD34D" : "#E2E8F0"}`, borderRadius: 10, padding: "14px 18px", cursor: "pointer" }}>
          <div style={{ fontSize: 28, fontWeight: 800, color: needsReviewCount > 0 ? "#D97706" : "#94A3B8" }}>{needsReviewCount}</div>
          <div style={{ fontSize: 12, color: "#64748B", fontWeight: 500 }}>Needs Review</div>
        </div>
        <div onClick={() => { setTab("pending"); setFilter("all"); }}
          style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 10, padding: "14px 18px", cursor: "pointer" }}>
          <div style={{ fontSize: 28, fontWeight: 800, color: "#15803D" }}>{data?.total || 0}</div>
          <div style={{ fontSize: 12, color: "#64748B", fontWeight: 500 }}>Total Pending</div>
        </div>
        <div onClick={() => setTab("reviewed")}
          style={{ background: "#F0F9FF", border: "1px solid #BAE6FD", borderRadius: 10, padding: "14px 18px", cursor: "pointer" }}>
          <div style={{ fontSize: 28, fontWeight: 800, color: "#0369A1" }}>{reviewedCount}</div>
          <div style={{ fontSize: 12, color: "#64748B", fontWeight: 500 }}>Reviewed</div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        <button onClick={() => setTab("pending")} className={tab === "pending" ? "btn btn-primary btn-sm" : "btn btn-ghost btn-sm"} style={{ fontSize: 12 }}>
          Pending {data?.total > 0 && `(${data.total})`}
        </button>
        <button onClick={() => setTab("reviewed")} className={tab === "reviewed" ? "btn btn-primary btn-sm" : "btn btn-ghost btn-sm"} style={{ fontSize: 12 }}>
          Reviewed {reviewedCount > 0 && `(${reviewedCount})`}
        </button>
      </div>

      {/* ── PENDING TAB ─────────────────────────────────────────────────────── */}
      {tab === "pending" && <>
        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          {[["all","All"],["critical","🔴 Critical Only"],["needs_review","⚠️ Needs Review Only"]].map(([val,label]) => (
            <button key={val} onClick={() => setFilter(val)}
              className={filter === val ? "btn btn-secondary btn-sm" : "btn btn-ghost btn-sm"}
              style={{ fontSize: 12 }}>{label}</button>
          ))}
        </div>

        {/* Search bar */}
        <div style={{ marginBottom: 14 }}>
          <input className="input" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by patient name, EMR ID, report type..."
            style={{ width: "100%", padding: "8px 12px", fontSize: 13 }} />
        </div>

        {filtered.length === 0 ? (
          <div style={{ textAlign: "center", padding: "48px 0", color: "#94A3B8" }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
            <div style={{ fontSize: 15, fontWeight: 600, color: "#64748B", marginBottom: 4 }}>All clear!</div>
            <div style={{ fontSize: 13 }}>No reports requiring attention.</div>
          </div>
        ) : filtered.map(report => (
          <ReportCard key={report.report_id} report={report} canAction={canAction}
            actioning={actioning} updatingStatus={updatingStatus}
            onAction={handleAction} onStatusChange={handleStatusChange}
            onViewReport={handleViewReport} userRole={user?.role} />
        ))}
      </>}

      {/* ── REVIEWED TAB ────────────────────────────────────────────────────── */}
      {tab === "reviewed" && <>
        {/* Clear all button */}
        {canAction && reviewed?.total > 0 && (
          <div style={{ marginBottom: 14 }}>
            {showClearConfirm ? (
              <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 8 }}>
                <span style={{ fontSize: 13, color: "#DC2626", flex: 1 }}>⚠ Are you sure you want to clear all {reviewed.total} reviewed reports from this list?</span>
                <button className="btn btn-danger btn-sm" onClick={handleClear} disabled={clearing} style={{ fontSize: 12 }}>
                  {clearing ? "Clearing..." : "Yes, Clear All"}
                </button>
                <button className="btn btn-ghost btn-sm" onClick={() => setShowClearConfirm(false)} style={{ fontSize: 12 }}>Cancel</button>
              </div>
            ) : (
              <button className="btn btn-ghost btn-sm" onClick={() => setShowClearConfirm(true)}
                style={{ fontSize: 12, color: "#DC2626", border: "1px solid #FECACA" }}>
                🗑 Clear All Reviewed
              </button>
            )}
          </div>
        )}

      {!reviewed ? (
          <div style={{ padding: 40, textAlign: "center", color: "#64748B", fontSize: 13 }}>Loading...</div>
        ) : reviewed.reports.length === 0 ? (
          <div style={{ textAlign: "center", padding: "48px 0", color: "#94A3B8" }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
            <div style={{ fontSize: 15, fontWeight: 600, color: "#64748B", marginBottom: 4 }}>No reviewed reports yet</div>
          </div>
        ) : reviewed.reports.map(report => (
          <div key={report.report_id} style={{ background: "#fff", border: "1px solid #E2E8F0", borderLeft: "4px solid #10B981", borderRadius: 10, marginBottom: 14, overflow: "hidden" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", background: "#F0FDF4", borderBottom: "1px solid #F1F5F9" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 99, background: "#10B981", color: "#fff" }}>✓ Reviewed</span>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>{report.patient_name}</div>
                  <div style={{ fontSize: 11, color: "#64748B" }}>{report.patient_id} · {report.patient_age}Y / {report.patient_sex} · {report.type} · {report.date}</div>
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: "#10B981" }}>Reviewed by {report.reviewed_by}</div>
                <div style={{ fontSize: 11, color: "#94A3B8" }}>{report.reviewed_at}</div>
              </div>
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <tbody>
                {report.values.map((v, i) => {
                  const sc = STATUS_COLORS[v.status] || STATUS_COLORS["Normal"];
                  return (
                    <tr key={i} style={{ borderTop: "1px solid #F1F5F9" }}>
                      <td style={{ padding: "8px 16px", fontWeight: 500 }}>{v.name}</td>
                      <td style={{ padding: "8px 12px", textAlign: "right", fontWeight: 700, color: sc.color }}>{v.value}</td>
                      <td style={{ padding: "8px 12px", color: "#64748B" }}>{v.unit}</td>
                      <td style={{ padding: "8px 12px", color: "#94A3B8", fontSize: 12 }}>{v.ref_text && v.ref_text.length > 20 ? (
                        <span className="ref-tip-wrap" style={{ cursor: "help" }}>
                          <span style={{ borderBottom: "1px dashed #94A3B8" }}>Multiple ranges</span>
                          <div className="ref-tip-box">{v.ref_text}</div>
                        </span>
                      ) : v.ref_text || (v.ref_min != null ? `${v.ref_min}–${v.ref_max}` : "—")}</td>
                      <td style={{ padding: "8px 16px" }}>
                        <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 99, background: sc.bg, color: sc.color, border: `1px solid ${sc.border}` }}>{v.status}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))}
      </>}

      <style>{`@keyframes pulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.6;transform:scale(1.1)} }`}</style>
    </div>
  );
}

function ReportCard({ report, canAction, actioning, updatingStatus, onAction, onStatusChange, onViewReport, userRole }) {
  const [confirming, setConfirming] = useState(false);

  return (
    <div style={{
      background: "#fff",
      border: `1px solid ${report.has_critical ? "#FCA5A5" : "#FCD34D"}`,
      borderLeft: `4px solid ${report.has_critical ? "#DC2626" : "#F59E0B"}`,
      borderRadius: 10, marginBottom: 14, overflow: "hidden",
      boxShadow: report.has_critical ? "0 2px 12px rgba(220,38,38,0.08)" : "0 2px 8px rgba(0,0,0,0.04)"
    }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", background: report.has_critical ? "#FFF5F5" : "#FFFDF0", borderBottom: "1px solid #F1F5F9" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {report.has_critical ? (
            <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 99, background: "#DC2626", color: "#fff", textTransform: "uppercase", letterSpacing: 1, animation: "pulse 1.5s infinite" }}>🔴 Critical</span>
          ) : (
            <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 99, background: "#F59E0B", color: "#fff", textTransform: "uppercase", letterSpacing: 1 }}>⚠ Review</span>
          )}
          <div>
            <div style={{ fontWeight: 700, fontSize: 14, color: "#0F172A" }}>{report.patient_name}</div>
            <div style={{ fontSize: 11, color: "#64748B" }}>
              {report.patient_id} · {report.patient_age}Y / {report.patient_sex} · {report.type} · {report.date}
              {report.lab_id && ` · Lab ID: ${report.lab_id}`}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <button className="btn btn-ghost btn-sm" onClick={() => onViewReport(report)} style={{ fontSize: 12 }}>
            👁 View Report
          </button>
          {canAction && (
            confirming ? (
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ fontSize: 12, color: "#64748B" }}>Mark as reviewed by {userRole === "admin" ? "Admin" : "Doctor"}?</span>
                <button className="btn btn-primary btn-sm" disabled={actioning === report.report_id}
                  onClick={() => { setConfirming(false); onAction(report.report_id); }}
                  style={{ fontSize: 12, background: "#10B981", border: "none" }}>
                  {actioning === report.report_id ? "..." : "Confirm"}
                </button>
                <button className="btn btn-ghost btn-sm" onClick={() => setConfirming(false)} style={{ fontSize: 12 }}>Cancel</button>
              </div>
            ) : (
              <button className="btn btn-primary btn-sm" onClick={() => setConfirming(true)}
                style={{ fontSize: 12, background: "#10B981", border: "none" }}>
                ✓ Mark Reviewed
              </button>
            )
          )}
        </div>
      </div>

      {/* Values table */}
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead>
          <tr style={{ background: "#F8FAFC" }}>
            <th style={{ padding: "7px 16px", textAlign: "left", fontWeight: 600, fontSize: 11, color: "#64748B", textTransform: "uppercase" }}>Parameter</th>
            <th style={{ padding: "7px 12px", textAlign: "right", fontWeight: 600, fontSize: 11, color: "#64748B", textTransform: "uppercase" }}>Value</th>
            <th style={{ padding: "7px 12px", textAlign: "center", fontWeight: 600, fontSize: 11, color: "#64748B", textTransform: "uppercase" }}>Unit</th>
            <th style={{ padding: "7px 12px", textAlign: "center", fontWeight: 600, fontSize: 11, color: "#64748B", textTransform: "uppercase" }}>Reference</th>
            <th style={{ padding: "7px 12px", textAlign: "center", fontWeight: 600, fontSize: 11, color: "#64748B", textTransform: "uppercase" }}>Status</th>
            {canAction && <th style={{ padding: "7px 16px", textAlign: "center", fontWeight: 600, fontSize: 11, color: "#64748B", textTransform: "uppercase" }}>Action</th>}
          </tr>
        </thead>
        <tbody>
          {report.values.map((v, i) => {
            const sc = STATUS_COLORS[v.status] || STATUS_COLORS["Normal"];
            const key = `${report.report_id}:${v.name}`;
            return (
              <tr key={i} style={{ borderTop: "1px solid #F1F5F9", background: (v.status === "Critical High" || v.status === "Critical Low") ? "#FFF5F5" : "transparent" }}>
                <td style={{ padding: "9px 16px", fontWeight: 500 }}>{v.name}</td>
                <td style={{ padding: "9px 12px", textAlign: "right", fontWeight: 700, color: sc.color }}>{v.value}</td>
                <td style={{ padding: "9px 12px", textAlign: "center", color: "#64748B" }}>{v.unit}</td>
                <td style={{ padding: "9px 12px", textAlign: "center", color: "#94A3B8", fontSize: 12 }}>
                  {v.ref_text && v.ref_text.length > 20 ? (
                    <span className="ref-tip-wrap" style={{ cursor: "help" }}>
                      <span style={{ borderBottom: "1px dashed #94A3B8", fontSize: 11 }}>Multiple ranges</span>
                      <div className="ref-tip-box">{v.ref_text}</div>
                    </span>
                  ) : v.ref_text || (v.ref_min != null ? `${v.ref_min}–${v.ref_max}` : "—")}
                </td>
                <td style={{ padding: "9px 12px", textAlign: "center" }}>
                  <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 99, background: sc.bg, color: sc.color, border: `1px solid ${sc.border}` }}>{v.status}</span>
                </td>
                {canAction && (
                  <td style={{ padding: "9px 16px", textAlign: "center" }}>
                    {(v.status === "Needs Review" || v.status === "Critical High" || v.status === "Critical Low") && (
                      <select disabled={updatingStatus === key}
                        onChange={e => e.target.value && onStatusChange(report.report_id, v.name, e.target.value)}
                        style={{ fontSize: 11, padding: "3px 6px", borderRadius: 5, border: "1px solid #E2E8F0", cursor: "pointer" }}
                        defaultValue="">
                        <option value="" disabled>Override...</option>
                        {["Normal","High","Low","Critical High","Critical Low","Abnormal","Needs Review"].map(s => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    )}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}